import React, { useReducer } from 'react';
import { ReducerActionMap } from '../types';
import { AvailableQueryParamKey, withQueryParams } from '../utils/withQueryParams';
import { useGlobalState } from './GlobalStateContext';

/**
 *  TYPES DECLARATION
 */

enum UrlTypes {
  SET_RELOAD_FILTERS = 'SET_RELOAD_FILTERS',
  SET_RELOAD_APPLICATION = 'SET_RELOAD_APPLICATION',
  SET_RELOAD_TOTAL = 'SET_RELOAD_TOTAL',
  SET_APPLICATION_STATUS = 'SET_APPLICATION_STATUS',
  SET_TOTAL_STATUS = 'SET_TOTAL_STATUS',
  SET_FILTERS_STATUS = 'SET_FILTERS_STATUS',
}

type Status = 'shouldLoad' | 'shouldCancel';

type UrlPayload = {
  // eslint-disable-next-line @typescript-eslint/ban-types
  [UrlTypes.SET_RELOAD_FILTERS]: null;
  [UrlTypes.SET_RELOAD_APPLICATION]: null;
  [UrlTypes.SET_RELOAD_TOTAL]: null;
  [UrlTypes.SET_APPLICATION_STATUS]: { status: Status };
  [UrlTypes.SET_TOTAL_STATUS]: { status: Status };
  [UrlTypes.SET_FILTERS_STATUS]: { status: Status };
};

type UrlActions = ReducerActionMap<UrlPayload>[keyof ReducerActionMap<UrlPayload>];

type InitialStateType = {
  reloadApplicationsTrigger: boolean;
  reloadApplicationsStatus: Status;
  reloadTotalTrigger: boolean;
  reloadTotalStatus: Status;
  reloadFiltersTrigger: boolean;
  reloadFiltersStatus: Status;
};

/**
 *  VARIABLES DECLARATION
 */

const initialState: InitialStateType = {
  reloadApplicationsTrigger: false,
  reloadApplicationsStatus: 'shouldCancel',
  reloadTotalTrigger: false,
  reloadTotalStatus: 'shouldCancel',
  reloadFiltersTrigger: false,
  reloadFiltersStatus: 'shouldLoad',
};

const RequestPriorityContext = React.createContext<
  | {
      state: InitialStateType;
    }
  | any
>({
  state: initialState,
});

RequestPriorityContext.displayName = 'RequestPriorityContext';

export const requestPriorityReducer = (state: InitialStateType, action: UrlActions): InitialStateType => {
  switch (action.type) {
    case UrlTypes.SET_RELOAD_FILTERS: {
      return {
        ...state,
        reloadFiltersTrigger: !state.reloadFiltersTrigger,
      };
    }
    case UrlTypes.SET_RELOAD_APPLICATION: {
      return {
        ...state,
        reloadApplicationsTrigger: !state.reloadApplicationsTrigger,
      };
    }
    case UrlTypes.SET_RELOAD_TOTAL: {
      return {
        ...state,
        reloadTotalTrigger: !state.reloadTotalTrigger,
      };
    }
    case UrlTypes.SET_TOTAL_STATUS: {
      const { status } = action.payload;
      return {
        ...state,
        reloadTotalStatus: status,
      };
    }
    case UrlTypes.SET_APPLICATION_STATUS: {
      const { status } = action.payload;
      return {
        ...state,
        reloadApplicationsStatus: status,
      };
    }
    case UrlTypes.SET_FILTERS_STATUS: {
      const { status } = action.payload;
      return {
        ...state,
        reloadFiltersStatus: status,
      };
    }
    default: {
      return state;
    }
  }
};

const QUERY_PARAM_KEYS = [
  'page',
  'applicationTypes',
  'architectureTypes',
  'countries',
  'sgMarkets',
  'trigrams',
  'tribes',
  'criticalities',
  'tags',
  'status',
  'query',
  'sort',
  'order',
  'resultSort',
  'resultOrder',
  'resultValue',
  'managedBy',
  'names',
  'functionalArchitecture',
  'isAmerManaged',
  'isCertified',
  'languages',
  'presenceResults',
  'novaCompatibility',
  'ownedBy',
  'infrastructureComponent',
  'obsolescenceDate',
  'scopeResults',
  'remediations',
  'ownershipRc',
  'managementRc',
  'enablers',
  'iam',
  'assetZones',
  'applicationRemediations',
] satisfies Array<AvailableQueryParamKey>;

const RequestPriorityProvider: React.FC<{ children: React.ReactNode }> = withQueryParams<
  { children: React.ReactNode },
  typeof QUERY_PARAM_KEYS
>(QUERY_PARAM_KEYS, ({ children, queryParams, setQueryParams }) => {
  const [state, dispatch] = useReducer(requestPriorityReducer, initialState);
  const { snapshot } = useGlobalState();

  React.useEffect(() => {
    if (state.reloadFiltersStatus !== 'shouldLoad') {
      setFiltersStatus('shouldLoad');
    }
  }, [snapshot]);

  React.useEffect(() => {
    reloadApplications();
    setTotalStatus('shouldCancel');
    if (queryParams.page) {
      setQueryParams({ page: undefined }, 'replaceIn');
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    queryParams.applicationTypes,
    queryParams.architectureTypes,
    queryParams.countries,
    queryParams.sgMarkets,
    queryParams.trigrams,
    queryParams.tribes,
    queryParams.criticalities,
    queryParams.tags,
    queryParams.status,
    queryParams.query,
    queryParams.sort,
    queryParams.order,
    queryParams.resultSort,
    queryParams.resultOrder,
    queryParams.resultValue,
    queryParams.managedBy,
    queryParams.names,
    queryParams.functionalArchitecture,
    queryParams.isAmerManaged,
    queryParams.isCertified,
    queryParams.languages,
    queryParams.presenceResults,
    queryParams.novaCompatibility,
    queryParams.ownedBy,
    queryParams.infrastructureComponent,
    queryParams.obsolescenceDate,
    queryParams.scopeResults,
    queryParams.remediations,
    queryParams.ownershipRc,
    queryParams.managementRc,
    queryParams.enablers,
    queryParams.iam,
    queryParams.assetZones,
    queryParams.applicationRemediations,
    snapshot,
  ]);

  const reloadFilters = React.useCallback(
    () => dispatch({ type: UrlTypes.SET_RELOAD_FILTERS, payload: null }),
    [dispatch]
  );

  const reloadApplications = React.useCallback(
    () => dispatch({ type: UrlTypes.SET_RELOAD_APPLICATION, payload: null }),
    [dispatch]
  );

  const reloadTotal = React.useCallback(() => dispatch({ type: UrlTypes.SET_RELOAD_TOTAL, payload: null }), [dispatch]);

  const setApplicationsStatus = React.useCallback(
    (status: Status) => dispatch({ type: UrlTypes.SET_APPLICATION_STATUS, payload: { status } }),
    [dispatch]
  );

  const setTotalStatus = React.useCallback(
    (status: Status) => dispatch({ type: UrlTypes.SET_TOTAL_STATUS, payload: { status } }),
    [dispatch]
  );

  const setFiltersStatus = React.useCallback(
    (status: Status) => dispatch({ type: UrlTypes.SET_FILTERS_STATUS, payload: { status } }),
    [dispatch]
  );

  const value = React.useMemo(
    () => ({
      ...state,
      reloadFilters,
      reloadTotal,
      reloadApplications,
      setApplicationsStatus,
      setTotalStatus,
      setFiltersStatus,
    }),
    [state, reloadFilters, reloadTotal, reloadApplications, setApplicationsStatus, setTotalStatus, setFiltersStatus]
  );

  return <RequestPriorityContext.Provider value={value}>{children}</RequestPriorityContext.Provider>;
});

export const useRequestPriority = (): {
  reloadApplications: () => void;
  reloadTotal: () => void;
  reloadFilters: () => void;
  setApplicationsStatus: (status: Status) => void;
  setFiltersStatus: (status: Status) => void;
  setTotalStatus: (status: Status) => void;
} & InitialStateType => {
  const context = React.useContext(RequestPriorityContext);
  if (context === undefined) {
    throw new Error(`useUrl must be used within an UrlProvider`);
  }
  return context;
};

export { RequestPriorityContext, RequestPriorityProvider };
